import { SAT, SATCircle, SATPoint, SATPolygon, SATShape } from 'engine/lib/sat';
import clamp from 'lodash/clamp';
import { Engine } from '../Engine';
import { moveAABB } from '../lib/AABB';
import { V2, addV2 } from '../lib/V2';
import { ColliderFacet, ColliderType } from './ColliderFacet';
import { Facet } from './Facet';

const colliderToSATShape = (c: ColliderFacet): SATShape => {
  switch (c.props.type) {
    case ColliderType.Box: {
      const s = new SATPolygon();
      const [x, y] = c.props.offset;
      const [width, height] = c.props.size;
      s.vertices = [
        new SATPoint(x, y),
        new SATPoint(x + width, y),
        new SATPoint(x + width, y + height),
        new SATPoint(x, y + height),
      ];
      s.x = c.entity.transform.position[0];
      s.y = c.entity.transform.position[1];
      return s;
    }
    case ColliderType.Circle: {
      const s = new SATCircle();
      const [x, y] = c.entity.transform.position;
      const [xOffset, yOffset] = c.props.offset;
      s.radius = c.props.radius;
      s.x = x + xOffset + c.props.radius;
      s.y = y + yOffset + c.props.radius;
      return s;
    }
    case ColliderType.Polygon: {
      const s = new SATPolygon();
      const [x, y] = c.entity.transform.position;
      const [xOffset, yOffset] = c.props.offset;
      s.vertices = c.props.points.map(p => new SATPoint(p[0], p[1]));
      s.x = x + xOffset;
      s.y = y + yOffset;
      return s;
    }
  }
};

interface KinematicBodyProps {
  constrainX?: V2,
  constrainY?: V2,
}

export class KinematicBodyFacet extends Facet<KinematicBodyProps> {

  initialPosition: V2 = [0, 0];

  start() {
    this.initialPosition = [...this.entity.transform.position];
  }

  move(amount: V2, engine: Engine) {
    const { constrainX, constrainY } = this.props;
    const { position } = this.entity.transform;
    addV2(this.entity.transform.position, amount);
    const [xInit, yInit] = this.initialPosition;
    if (constrainX) position[0] = clamp(position[0], constrainX[0] + xInit, constrainX[1] + xInit);
    if (constrainY) position[1] = clamp(position[1], constrainY[0] + yInit, constrainY[1] + yInit);

    this.entity.getFacets(ColliderFacet).forEach(ownC => {
      const aabb1 = moveAABB(ownC.aabb, position);
      engine.entities.forEach(entity => {
        if (entity === this.entity) return;
        entity.getFacets(ColliderFacet).forEach(c => {
          if (c.props.layer && ownC.props.layer && c.props.layer !== ownC.props.layer) return;
          const aabb2 = moveAABB(c.aabb, entity.transform.position);
          if (
            aabb1.position[0] < aabb2.position[0] + aabb2.size[0]
            && aabb1.position[0] + aabb1.size[0] > aabb2.position[0]
            && aabb1.position[1] < aabb2.position[1] + aabb2.size[1]
            && aabb1.position[1] + aabb1.size[1] > aabb2.position[1]
          ) {
            const collisionInfo = SAT.test(
              colliderToSATShape(ownC),
              colliderToSATShape(c),
            );
            if (collisionInfo) {
              c.collisions.push(ownC);
              ownC.collisions.push(c);
              if (c.props.trigger || ownC.props.trigger) return;
              addV2(position, [
                collisionInfo.separation.x * -1,
                collisionInfo.separation.y * -1,
              ]);
            }
          }
        });
      });
    });
  }
}
